/**
 * Video utilities for handling thumbnails and video optimization
 */

/**
 * Get the thumbnail path for a feature title
 * 
 * @param {string} featureTitle - The title of the feature
 * @returns {string} The path to the thumbnail
 */
export const getVideoThumbnail = (featureTitle) => {
  // Create a sanitized filename from the feature title
  const sanitizedTitle = featureTitle.replace(/\s+/g, '-').toLowerCase();
  
  // Return the path to where the thumbnail should be
  return `/thumbnails/${sanitizedTitle}.jpg`;
};

/**
 * Get a thumbnail from a video file
 * 
 * @param {string} videoSrc - The video URL
 * @returns {Promise<string>} A promise that resolves to a data URL of the thumbnail
 */
export const generateThumbnailFromVideo = (videoSrc) => {
  return new Promise((resolve, reject) => {
    // Create video element
    const video = document.createElement('video');
    video.src = videoSrc;
    video.crossOrigin = 'anonymous'; // Handle CORS if needed
    video.muted = true;
    video.preload = 'metadata';
    
    // When video metadata is loaded
    video.onloadedmetadata = () => {
      // Seek to the 2 second mark
      video.currentTime = 2;
    };
    
    // When the video frame at the specified time is loaded
    video.onseeked = () => {
      // Create canvas to capture the frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw the video frame to the canvas
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to data URL
      try {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(dataUrl);
      } catch (e) {
        reject(new Error('Could not generate thumbnail: ' + e.message));
      }
      
      // Clean up
      video.pause();
      video.src = '';
      video.load();
    };
    
    // Handle errors
    video.onerror = () => {
      reject(new Error('Error loading video for thumbnail generation'));
    };
    
    // Start loading the video
    video.load();
  });
};

/**
 * Lazy load a video source
 * 
 * @param {string} videoSrc - The video URL to load
 * @param {HTMLVideoElement} videoElement - The video element to update
 * @returns {Promise<void>} A promise that resolves when the video is loaded
 */
export const lazyLoadVideo = (videoSrc, videoElement) => {
  return new Promise((resolve, reject) => {
    if (!videoElement) {
      reject(new Error('No video element provided'));
      return;
    }
    
    // Set the video source
    videoElement.src = videoSrc;
    
    // When the video is loaded
    videoElement.onloadeddata = () => {
      resolve();
    };
    
    // Handle errors
    videoElement.onerror = () => {
      reject(new Error('Error loading video'));
    };
    
    // Load the video
    videoElement.load();
  });
};

/**
 * Check if a thumbnail exists at the path
 * 
 * @param {string} thumbnailPath - The path to check
 * @returns {Promise<boolean>} A promise that resolves to true if the thumbnail exists
 */
export const checkThumbnailExists = (thumbnailPath) => {
  return new Promise((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      resolve(true);
    };
    
    img.onerror = () => {
      resolve(false);
    };
    
    img.src = thumbnailPath;
  });
}; 